import React from 'react';
import { useTranslation } from 'react-i18next';
import type {
    AssetCategory,
    ChartAccount,
    FixedAsset,
    LocaleCode,
} from '@/api/models';
import type { FixedAssetStatusFilter } from '@/api/handlers/resources/fixedAssets/requests';
import type { ErrorResponse } from '@/api/handlers/responses';
import { useDebouncedValue } from '@/utils/useDebouncedValue';
import {
    useAssetCategoriesQuery,
    useChartOfAccounts,
    useFixedAssetsQuery,
    useReconciliationQuery,
} from '@/api/queries';
import {
    resolveLocale,
    unwrapStatement,
} from '@/pages/accounting/statements/statementMeta';
import {
    toTieOut,
    registerEmptyState,
    toAssetRows,
    toSummaryFigures,
    type AssetRow,
    type RegisterEmptyState,
    type RegisterTieOut,
    type SummaryFigures,
} from './fixedAssetsMeta';

/** Long enough to collapse a burst of typing, short enough to feel immediate. */
const SEARCH_DEBOUNCE_MS = 250;

/** Which drawer / dialog is open. Only one at a time. */
type OverlayState =
    | { kind: 'NONE' }
    | { kind: 'DETAIL'; assetId: string }
    | { kind: 'FORM'; asset: FixedAsset | null; instanceId: number }
    | { kind: 'DISPOSE'; asset: FixedAsset }
    | { kind: 'RUN' };

export type UseFixedAssetsPageResult = {
    locale: LocaleCode;
    isLoading: boolean;
    error: ErrorResponse | null;
    onRetry: () => void;
    status: FixedAssetStatusFilter;
    search: string;
    onStatusChange: (status: FixedAssetStatusFilter) => void;
    onSearchChange: (search: string) => void;
    /** Null until the first load resolves. */
    figures: SummaryFigures | null;
    /** Null while the reconciliation is still loading **or** has failed. */
    tieOut: RegisterTieOut | null;
    /**
     * Non-null when the tie-out could not be read at all.
     *
     * Distinct from `tieOut === null`, which means "not known yet". A failed
     * reconciliation must not sit under a "Checking…" label forever: an unknown
     * ledger is not a disagreeing one, but it is not a pending one either, and
     * the difference between them is exactly what this bar exists to report.
     */
    tieOutError: ErrorResponse | null;
    /** Retry the tie-out alone, without refetching the whole register. */
    onRetryTieOut: () => void;
    rows: AssetRow[];
    emptyState: RegisterEmptyState;
    categories: AssetCategory[];
    accounts: ChartAccount[];
    overlay: OverlayState;
    onOpenDetail: (assetId: string) => void;
    onOpenCreate: () => void;
    onOpenEdit: (assetId: string) => void;
    onOpenDispose: (assetId: string) => void;
    onOpenRun: () => void;
    onCloseOverlay: () => void;
};

/**
 * Drives the Fixed Assets register.
 *
 * It reads two independent feeds and joins them only for the tie-out: the
 * register (its own totals) and the **existing** chart-of-accounts / trial-balance
 * query (the general ledger). That separation is deliberate — the register is not
 * allowed to certify itself, so the comparison is made against the same balances
 * every other accounting screen reads.
 *
 * All derivation is pure and lives in `fixedAssetsMeta`; this hook owns fetching,
 * filter state and which overlay is open.
 */
export const useFixedAssetsPage = (): UseFixedAssetsPageResult => {
    const { i18n } = useTranslation();
    const locale = resolveLocale(i18n.language);

    const [status, setStatus] =
        React.useState<FixedAssetStatusFilter>('ACTIVE');
    const [search, setSearch] = React.useState('');
    const [overlay, setOverlay] = React.useState<OverlayState>({
        kind: 'NONE',
    });
    /** Bumped on each form open so a reopened drawer never inherits a draft. */
    const formInstanceRef = React.useRef(0);

    // The register is filtered server-side (the real endpoint takes `search`),
    // so the query key holds a debounced copy: typing "compressor" should cost
    // one request, not ten. The input stays fully controlled by `search`.
    const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);

    const assetsQuery = useFixedAssetsQuery({
        status,
        search: debouncedSearch,
    });
    const categoriesQuery = useAssetCategoriesQuery();
    const reconciliationQuery = useReconciliationQuery();
    // Still read: the funding and proceeds account pickers need the chart. It is
    // no longer the tie-out's ledger side — that comes from the server.
    const chart = useChartOfAccounts({ locale });

    const { data: assetsData, error: assetsError } = unwrapStatement(
        assetsQuery.data,
        assetsQuery.error,
    );
    const { data: categoriesData } = unwrapStatement(
        categoriesQuery.data,
        categoriesQuery.error,
    );
    const { data: reconciliationData, error: reconciliationError } =
        unwrapStatement(reconciliationQuery.data, reconciliationQuery.error);

    // Memoised because it feeds a `useMemo` below: a bare `?? []` mints a
    // fresh array on every render, so every dependent recomputes forever.
    const categories = React.useMemo(
        () => categoriesData?.items ?? [],
        [categoriesData],
    );
    const accounts = chart.data ?? [];

    const figures = React.useMemo(
        () => (assetsData ? toSummaryFigures(assetsData.summary) : null),
        [assetsData],
    );

    // The tie-out is the server's now, not a client-side comparison of the
    // register's totals against the trial balance: `GET /reconciliation` holds
    // both sides and states the verdict. See `toTieOut`.
    const tieOut = React.useMemo(
        () => toTieOut(reconciliationData ?? null),
        [reconciliationData],
    );

    const rows = React.useMemo(
        () =>
            assetsData ? toAssetRows(assetsData.items, categories, locale) : [],
        [assetsData, categories, locale],
    );

    const emptyState = React.useMemo<RegisterEmptyState>(() => {
        if (!assetsData) {
            return 'NONE';
        }
        return registerEmptyState({
            visibleCount: assetsData.items.length,
            activeCount: assetsData.summary.assetCount,
            hasSearch: debouncedSearch.trim() !== '',
            // `totalAssetCount` ignores the filter on purpose. Deriving this from
            // the filtered row count made a Disposed filter over a register with
            // no disposals report "No assets yet" — which was false, and is the
            // reason the summary carries an unfiltered total at all.
            totalCount: assetsData.summary.totalAssetCount,
        });
    }, [assetsData, debouncedSearch]);

    const assetById = React.useCallback(
        (assetId: string): FixedAsset | null =>
            assetsData?.items.find((asset) => asset.id === assetId) ?? null,
        [assetsData],
    );

    const onOpenDetail = React.useCallback((assetId: string) => {
        setOverlay({ kind: 'DETAIL', assetId });
    }, []);

    const onOpenCreate = React.useCallback(() => {
        formInstanceRef.current += 1;
        setOverlay({
            kind: 'FORM',
            asset: null,
            instanceId: formInstanceRef.current,
        });
    }, []);

    const onOpenEdit = React.useCallback(
        (assetId: string) => {
            const asset = assetById(assetId);
            if (!asset) {
                return;
            }
            formInstanceRef.current += 1;
            setOverlay({
                kind: 'FORM',
                asset,
                instanceId: formInstanceRef.current,
            });
        },
        [assetById],
    );

    const onOpenDispose = React.useCallback(
        (assetId: string) => {
            const asset = assetById(assetId);
            if (asset) {
                setOverlay({ kind: 'DISPOSE', asset });
            }
        },
        [assetById],
    );

    const onOpenRun = React.useCallback(() => {
        setOverlay({ kind: 'RUN' });
    }, []);

    const onCloseOverlay = React.useCallback(() => {
        setOverlay({ kind: 'NONE' });
    }, []);

    const onRetry = React.useCallback(() => {
        assetsQuery.refetch();
        categoriesQuery.refetch();
        chart.refetch();
        // The tie-out has its own feed and its own failure — a retry that left
        // it out would clear the register's error while the bar stayed stuck.
        reconciliationQuery.refetch();
    }, [assetsQuery, categoriesQuery, chart, reconciliationQuery]);

    const onRetryTieOut = React.useCallback(() => {
        reconciliationQuery.refetch();
    }, [reconciliationQuery]);

    return {
        locale,
        isLoading: assetsQuery.isLoading || categoriesQuery.isLoading,
        error: assetsError,
        onRetry,
        status,
        search,
        onStatusChange: setStatus,
        onSearchChange: setSearch,
        figures,
        tieOut,
        tieOutError: reconciliationError,
        onRetryTieOut,
        rows,
        emptyState,
        categories,
        accounts,
        overlay,
        onOpenDetail,
        onOpenCreate,
        onOpenEdit,
        onOpenDispose,
        onOpenRun,
        onCloseOverlay,
    };
};
